import { ArrowUpRight, Check } from "lucide-react";
import { FadeUp } from "@/components/motion/Reveal";
import { CASE_STUDY } from "@/data/content";

const Card = ({ item }) => (
  <FadeUp
    className={`group relative overflow-hidden border border-line bg-surface ${item.span} ${
      item.tall ? "min-h-[400px] md:min-h-[500px]" : "min-h-[320px] md:min-h-[380px]"
    }`}
  >
    <img
      src={item.img}
      alt={item.title}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-[900ms] ease-out group-hover:scale-105"
    />
    {/* dark bottom gradient keeps text readable while imagery stays identifiable */}
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />

    <div className="relative z-10 flex h-full flex-col justify-between p-7 md:p-8">
      <div className="flex items-start justify-between">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-signal">{item.index}</span>
        <ArrowUpRight className="h-6 w-6 text-chalk opacity-0 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:opacity-100" />
      </div>
      <div>
        <h3 className="font-display text-2xl font-medium uppercase leading-none tracking-tight text-chalk md:text-[26px]">
          {item.title}
        </h3>
        <p className="mt-3 max-w-md text-[15px] leading-[1.55] text-chalk/80">{item.desc}</p>
      </div>
    </div>
  </FadeUp>
);

const CaseStudy = () => {
  const cs = CASE_STUDY;
  return (
    <section id="casestudy" data-testid="ecosystem-section" className="border-t border-line bg-ink">
      <div className="mx-auto max-w-[1600px] scroll-mt-24 px-6 py-24 md:px-12 md:py-36">
        <FadeUp className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-[12px] uppercase tracking-[0.2em] text-signal">{cs.eyebrow}</p>
          <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-ash">
            {cs.roleLabel} · {cs.years}
          </p>
        </FadeUp>

        <FadeUp className="mb-10 max-w-4xl">
          <h2 className="font-display text-6xl font-semibold uppercase leading-[0.85] tracking-tightest text-chalk sm:text-7xl md:text-8xl">
            {cs.title}
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-[1.65] text-ash md:text-base">{cs.intro}</p>
        </FadeUp>

        {/* Three-column summary */}
        <div className="mb-12 grid grid-cols-1 gap-px overflow-hidden border border-line bg-line md:grid-cols-3">
          {cs.summary.map((col) => (
            <FadeUp key={col.label} className="bg-surface p-7 md:p-8">
              <p className="mb-4 font-mono text-[12px] uppercase tracking-[0.2em] text-signal">{col.label}</p>
              <p className="text-[15px] leading-[1.6] text-chalk/85">{col.body}</p>
            </FadeUp>
          ))}
        </div>

        {/* Process line */}
        <FadeUp className="mb-16 border border-line bg-surface p-6 md:p-7">
          <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.2em] text-ash">Process</p>
          <ol className="flex flex-wrap items-center gap-x-3 gap-y-3">
            {cs.process.map((step, i) => (
              <li key={step} className="flex items-center gap-3">
                <span className="font-display text-lg font-medium uppercase tracking-tight text-chalk md:text-xl">
                  {step}
                </span>
                {i < cs.process.length - 1 && <span className="text-signal">&rarr;</span>}
              </li>
            ))}
          </ol>
        </FadeUp>

        <FadeUp className="mb-8">
          <p className="font-mono text-[12px] uppercase tracking-[0.2em] text-ash">What I contributed</p>
        </FadeUp>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {cs.contributions.map((item) => (
            <Card key={item.index} item={item} />
          ))}
        </div>

        {/* Outcomes */}
        <FadeUp className="mt-14 border-t border-line pt-10">
          <p className="mb-6 font-mono text-[12px] uppercase tracking-[0.2em] text-signal">Outcomes</p>
          <ul className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {cs.outcomes.map((o) => (
              <li key={o} className="flex items-start gap-3">
                <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center border border-signal">
                  <Check className="h-3 w-3 text-signal" />
                </span>
                <span className="text-[15px] leading-[1.55] text-chalk/85">{o}</span>
              </li>
            ))}
          </ul>
        </FadeUp>
      </div>
    </section>
  );
};

export default CaseStudy;
